import type { PaymentIntentConfirmParams, Stripe } from '@stripe/stripe-js'
import type { ConfirmPaymentData } from '@stripe/stripe-js/types/stripe-js/payment-intents'

import type { IdentityFormData } from '@/helpers/IdentityHelpers'

import { stripePayment } from '@/checkout/helpers/payloads'
import { CheckoutResult, checkout } from '@/checkout/helpers/processing'
import TixStripeError from '@/checkout/stripe/TixStripeError'
import { PaymentMethodName, billingDetails } from '@/checkout/stripe/helpers'
import { logInTrackJS } from '@/errors/helpers'

export function handlePaymentRedirectResponse(
  stripe: Stripe,
  cartId: string,
  clientSecret: string,
  sourceType: PaymentMethodName,
  options,
): Promise<CheckoutResult> {
  return stripe.retrievePaymentIntent(clientSecret).then((response) => {
    if (response.error) {
      throw new TixStripeError(response.error)
    } else {
      const paymentIntent = response.paymentIntent
      if (['requires_capture', 'processing', 'succeeded'].includes(paymentIntent.status)) {
        // TODO Test this for other currencies
        // PaymentIntent amount is in cents/pence.
        const payload = stripePayment(paymentIntent.amount / 100, paymentIntent.id, sourceType, false)
        return checkout(cartId, payload, options)
      } else if (paymentIntent!.last_payment_error) {
        throw new TixStripeError(paymentIntent!.last_payment_error)
      } else {
        // Not sure if it is possible to end up here.
        logInTrackJS('Unexpected Payment Redirect Error', paymentIntent)
        throw new Error(paymentIntent!.status)
      }
    }
  })
}

export function getRedirectPaymentData(
  cartId: string,
  identityFormData: IdentityFormData,
  purchaserId?: string,
): ConfirmPaymentData {
  return {
    shipping: getShippingParams(identityFormData),
    return_url: `${location.origin}/checkout?cartId=${cartId}&purchaser=${purchaserId}`,
    payment_method_data: {
      billing_details: billingDetails(identityFormData),
    },
  }
}

function getShippingParams(identityFormData: IdentityFormData): PaymentIntentConfirmParams.Shipping | null {
  if (!identityFormData.address) {
    return null
  }

  const name =
    typeof identityFormData.name === 'string' && identityFormData.name.trim().length > 0
      ? identityFormData.name.trim()
      : [identityFormData.first_name, identityFormData.last_name]
          .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
          .join(' ')

  if (!name) {
    return null
  }

  return {
    name,
    address: {
      line1: identityFormData.address as string,
      city: identityFormData.city as string,
      state: identityFormData.state as string,
      country: identityFormData.country as string,
      postal_code: identityFormData.zip_code as string,
    },
  }
}
